import type {ClientTool} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolCallResult, ToolResultTextContent} from '@microsoft/agent-host-protocol'

import type {AgentSession} from './agent-session-list'
import {currentOrigin, sessionUrl} from './session-url'
import {SESSION_TITLE_MAX_LENGTH, truncateByCodePoint} from './session-title-tool'

export const LIST_SESSIONS_TOOL_NAME = 'list_sessions'

/** How many sessions to surface, to keep the model's context small. */
export const LIST_SESSIONS_LIMIT = 20

/** Compact, model-facing view of a session. */
export interface SessionSummary {
  id: string
  title: string
  state: string
  url: string
}

export interface ListSessionsClientToolDeps {
  /** List the user's cloud agent sessions (defaults to the real CAPI call). */
  listSessions: () => Promise<AgentSession[]>
  /** Page origin used to render full, openable session URLs (defaults to the browser origin). */
  origin?: string
  /**
   * Whether the companion `view_sessions` tool is also registered. When false,
   * the model is told to present the openable URL instead of calling a tool that
   * is not in its tool set. Defaults to true.
   */
  canOpenViaViewTool?: boolean
}

function buildListSessionsToolDefinition(canOpenViaViewTool: boolean): ClientTool['definition'] {
  const description = canOpenViaViewTool
    ? "List the user's recent Copilot cloud agent sessions (most recently updated first) so you can help " +
      'them find and open one. These are the background cloud agent sessions (including any started by ' +
      "create_session), separate from this chat. The result gives you each session's id, which you can pass " +
      'to view_sessions to open a specific one, plus an openable URL you can share with the user. Do not ' +
      'invent session ids.'
    : "List the user's recent Copilot cloud agent sessions (most recently updated first) so you can help " +
      'them find and open one. These are the background cloud agent sessions (including any started by ' +
      "create_session), separate from this chat. The result gives you each session's id and an openable URL " +
      'you can share with the user. Do not invent session ids.'
  return {
    name: LIST_SESSIONS_TOOL_NAME,
    title: 'List sessions',
    description,
    inputSchema: {
      type: 'object',
      properties: {},
    },
    outputSchema: {
      type: 'object',
      properties: {
        sessions: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: {type: 'string'},
              title: {type: 'string'},
              state: {type: 'string'},
              url: {type: 'string'},
            },
            required: ['id', 'title', 'state', 'url'],
          },
        },
      },
      required: ['sessions'],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: true,
    },
  }
}

/**
 * `ClientTool` implementation of `list_sessions`. Reads the user's Copilot cloud
 * agent sessions from Mission Control (browser side, with the user's credentials)
 * and returns a compact summary with each session's id and an openable link. The
 * relay runtime owns routing and completion.
 */
export function buildListSessionsClientTool(deps: ListSessionsClientToolDeps): ClientTool {
  const canOpenViaViewTool = deps.canOpenViaViewTool ?? true
  return {
    definition: buildListSessionsToolDefinition(canOpenViaViewTool),
    fulfil: () => fulfilListSessions(deps, canOpenViaViewTool),
  }
}

async function fulfilListSessions(
  deps: ListSessionsClientToolDeps,
  canOpenViaViewTool: boolean,
): Promise<ToolCallResult> {
  try {
    const tasks = await deps.listSessions()
    const sessions = toSessionSummaries(tasks, deps.origin ?? currentOrigin())
    return {
      success: true,
      pastTenseMessage: sessions.length === 1 ? 'Listed 1 session' : `Listed ${sessions.length} sessions`,
      structuredContent: {sessions},
      content: [textToolContent(renderSessionsForModel(sessions, canOpenViaViewTool))],
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Failed to list sessions',
      error: {message},
      content: [textToolContent(message)],
    }
  }
}

export function toSessionSummaries(sessions: AgentSession[], origin = ''): SessionSummary[] {
  return sessions
    .slice()
    .sort((a, b) => updatedAtMillis(b) - updatedAtMillis(a))
    .slice(0, LIST_SESSIONS_LIMIT)
    .map(session => ({
      id: session.taskId,
      title: sessionTitle(session),
      state: session.state,
      url: sessionUrl(session.taskId, origin, session.nwo),
    }))
}

function updatedAtMillis(session: AgentSession): number {
  const parsed = Date.parse(session.updatedAt)
  return Number.isNaN(parsed) ? 0 : parsed
}

function sessionTitle(session: AgentSession): string {
  // The title can fall back upstream to a long, user-provided prompt, so bound it
  // to the same max as a real session title so a few large prompts can't overflow
  // the model's context.
  return truncateByCodePoint(singleLine(session.title || 'Untitled session'), SESSION_TITLE_MAX_LENGTH)
}

/**
 * Collapse control characters and Unicode line/paragraph separators (including
 * newlines, U+2028, and U+2029) to spaces so a user-provided title/prompt
 * preview cannot fabricate extra rows or ids in the line-oriented, model-visible
 * result.
 */
function singleLine(value: string): string {
  const cleaned = value.replace(/[\p{Cc}\p{Zl}\p{Zp}]+/gu, ' ').trim()
  return cleaned || 'Untitled session'
}

function renderSessionsForModel(sessions: SessionSummary[], canOpenViaViewTool: boolean): string {
  if (sessions.length === 0) {
    return 'The user has no cloud agent sessions yet. Let them know they have no sessions.'
  }
  const lines = sessions.map(session => `- ${session.id} - ${session.title} [${session.state}] (${session.url})`)
  const header = canOpenViaViewTool
    ? 'The user has these cloud agent sessions. Pass a session id to view_sessions to open one, or share its URL:'
    : 'The user has these cloud agent sessions. Share a session URL with the user to open one:'
  return [header, ...lines].join('\n')
}

function textToolContent(text: string): ToolResultTextContent {
  return {type: 'text', text} as ToolResultTextContent
}
